import React, { useState, useEffect } from 'react';
import { Button, Select, Form, Typography, Alert, Radio, Space, Spin } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { selectOperator, setOperatorInjured } from '../../../redux/reducers/operatorInfoSlice';
import Api from '../../../services/api';
import { Operator } from '../../../types/api.types';

const { Title, Text } = Typography;
const { Option } = Select;

interface OperatorSelectionStepProps {
  onNext: () => void;
  onPrevious: () => void;
  isFirstStep: boolean;
  isLastStep: boolean;
}

const OperatorSelectionStep: React.FC<OperatorSelectionStepProps> = ({ 
  onNext, 
  onPrevious 
}) => {
  const dispatch = useAppDispatch();
  const auth = useAppSelector((state) => state.auth);
  const operatorInfo = useAppSelector((state) => state.operatorInfo);
  const [form] = Form.useForm();
  const [operators, setOperators] = useState<Operator[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchOperators();
  }, []);

  const fetchOperators = async () => {
    try {
      setLoading(true);
      setError(null);
      const operatorList = await Api.getOperators();
      setOperators(operatorList);
      
      // Set default operator if current user is in the list
      const currentUserOperator = operatorList.find(op => op.userId === auth.userData?.id);
      if (currentUserOperator) {
        form.setFieldsValue({ operatorId: currentUserOperator.id });
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load operators');
      console.error('Failed to fetch operators:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (values: any) => {
    const selectedOperatorData = operators.find(op => op.id === values.operatorId);
    
    if (selectedOperatorData) {
      dispatch(selectOperator(selectedOperatorData));
    }
    dispatch(setOperatorInjured(values.isInjured));

    onNext();
  };

  const renderOperatorOption = (operator: any) => (
    <Option key={operator.id} value={operator.id}>
      <div>
        <div style={{ fontWeight: 500 }}>{`${operator.firstName} ${operator.lastName}`}</div>
        <div style={{ fontSize: '12px', color: '#666' }}>{operator.email}</div>
      </div>
    </Option>
  );

  return (
    <div className="step-container">
      <Title level={3}>Operator Information</Title>
      <Text type="secondary">Select the operator involved in this incident</Text>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
          <p style={{ marginTop: 16 }}>Loading operators...</p>
        </div>
      ) : error ? (
        <Alert
          message="Error Loading Operators"
          description={error}
          type="error"
          showIcon
          action={
            <Button size="small" onClick={fetchOperators}>
              Retry
            </Button>
          }
          style={{ marginTop: 20 }}
        />
      ) : (
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          isInjured: operatorInfo.operatorInjured || false,
        }}
        style={{ marginTop: 20 }}
      >
        <Form.Item
          label="Select Operator"
          name="operatorId"
          rules={[{ required: true, message: 'Please select an operator' }]}
        >
          <Select
            size="large"
            placeholder="Select operator"
            showSearch
            filterOption={(input, option) =>
              (option?.children as any)?.props?.children?.[0]?.props?.children
                ?.toLowerCase()
                ?.includes(input.toLowerCase())
            }
          >
            {operators.map(renderOperatorOption)}
          </Select>
        </Form.Item>

        <Form.Item
          label="Injury Status"
          name="isInjured"
          rules={[{ required: true, message: 'Please indicate if there was an injury' }]}
        >
          <Radio.Group size="large">
            <Space direction="vertical">
              <Radio value={false}>No injury</Radio>
              <Radio value={true}>Injury occurred</Radio>
            </Space>
          </Radio.Group>
        </Form.Item>

        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue }) => {
            const isInjured = getFieldValue('isInjured');
            return isInjured && (
              <Alert
                message="Injury Reported"
                description="Since an injury has been reported, additional documentation may be required. Please ensure all relevant details are captured in the incident details section."
                type="warning"
                showIcon
                style={{ marginBottom: 20 }}
              />
            );
          }}
        </Form.Item>

        <div className="step-actions" style={{ marginTop: 40 }}>
          <Button onClick={onPrevious}>
            Previous
          </Button>
          <Button 
            type="primary" 
            htmlType="submit"
            icon={<UserOutlined />}
          >
            Continue to Photos
          </Button>
        </div>
      </Form>
      )}
    </div>
  );
};

export default OperatorSelectionStep;